import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  useAppDispatch,
  useAppSelector,
  type RootState,
} from "../../store/reducer";
import { useGetResources } from "../../hooks/useGetResources";
import PageLoader from "@helsenorge/core-framework/page-loader";
import type { Oppgave } from "../../types/oppgave";
import {
  fetchAktiveOppgaver,
  fetchHistoriskeOppgaver,
} from "../../store/thunks/thunk-oppgaveListe";
import Panel, {
  PanelStatus,
} from "@helsenorge/designsystem-react/components/Panel";
import PanelList from "@helsenorge/designsystem-react/components/PanelList";
import styles from "../../styles/oppgave-list.module.scss";
import StatusDot from "@helsenorge/designsystem-react/components/StatusDot";
import Button from "@helsenorge/designsystem-react/components/Button";
import EmptyState from "@helsenorge/designsystem-react/components/EmptyState";
import Icon from "@helsenorge/designsystem-react/components/Icon";
import ArrowRight from "@helsenorge/designsystem-react/components/Icons/ArrowRight";
import NoEye from "@helsenorge/designsystem-react/components/Icons/NoEye";
import Badge from "@helsenorge/designsystem-react/components/Badge";
import SharingStatus from "@helsenorge/designsystem-react/components/SharingStatus";
import {
  createDateString,
  createDateString_deadline,
} from "../../utils/date-utils";
import {
  getStatusText,
  getStatusDotVariant,
  getOppgaveInstruksjon,
} from "../../utils/details-utils";
import { TabType } from "../../enums/tabType";
import { OppgaveStatus } from "../../enums/oppgaveStatus";
import { OPPGAVER_PATH } from "HNEntities/url-types";
import { getSkjulOppgave } from "utils/visning-utils";

export const OppgaveListe = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const resources = useGetResources();
  const aktiveOppgaver = useAppSelector(
    (state) => state.oppgaveListe.aktiveOppgaver,
  );
  const historiskeOppgaver = useAppSelector(
    (state) => state.oppgaveListe.historiskeOppgaver,
  );
  const isAktiveLoading = useAppSelector(
    (state) => state.oppgaveListe.isAktiveLoading,
  );
  const isHistoriskeLoading = useAppSelector(
    (state) => state.oppgaveListe.isHistoriskeLoading,
  );
  const antallHistoriskeOppgaverHentet = useAppSelector(
    (state) => state.oppgaveListe.antallHistoriskeOppgaverHentet,
  );
  const tabType = useAppSelector(
    (state: RootState) => state.oppgaveListe.activeTabType,
  );
  const oppgaver =
    tabType === TabType.Aktuelle ? aktiveOppgaver : historiskeOppgaver;
  const isLoading =
    tabType === TabType.Aktuelle ? isAktiveLoading : isHistoriskeLoading;
  const visFlereOppgaverKnapp =
    tabType === TabType.Tidligere && antallHistoriskeOppgaverHentet >= 20;

  const [pageNumber, setPageNumber] = React.useState(1);

  const skjulOppgave = (oppgave: Oppgave): boolean =>
    getSkjulOppgave(oppgave?.SkjulForFullmakt);
  const visFrist = (status: OppgaveStatus): boolean => {
    return (
      status !== OppgaveStatus.Cancelled &&
      status !== OppgaveStatus.EnteredInError &&
      status !== OppgaveStatus.Completed
    );
  };
  const visFullfort = (status: OppgaveStatus): boolean => {
    return status === OppgaveStatus.Completed;
  };
  const visKansellert = (status: OppgaveStatus): boolean => {
    return (
      status === OppgaveStatus.Cancelled ||
      status === OppgaveStatus.EnteredInError
    );
  };
  const visInstruksjon = (integrertVisning: boolean | undefined): boolean => {
    return !integrertVisning;
  };

  //Hver gang man bytter mellom faner, resetter state for isInitialLoad og pageNumber
  useEffect(() => {
    setPageNumber(1);
  }, [tabType]);

  useEffect(() => {
    if (tabType === TabType.Aktuelle) {
      dispatch(fetchAktiveOppgaver());
    }
  }, [dispatch, tabType]);

  useEffect(() => {
    if (tabType === TabType.Tidligere) {
      dispatch(fetchHistoriskeOppgaver(pageNumber));
    }
  }, [dispatch, tabType, pageNumber]);

  if (isLoading && oppgaver.length === 0) {
    return <PageLoader />;
  }

  return (
    <div>
      {!isLoading && oppgaver.length === 0 && (
        <EmptyState
          title={
            tabType === TabType.Aktuelle
              ? resources?.emptyState_aktuelle_oppgaver
              : resources?.emptyState_tidligere_oppgaver
          }
        ></EmptyState>
      )}
      <PanelList>
        {oppgaver?.map(
          (oppgave: Oppgave) =>
            !skjulOppgave(oppgave) && (
              <Panel
                key={oppgave.Id}
                status={!oppgave.Lest ? PanelStatus.new : PanelStatus.none}
              >
                <Panel.Title
                  title={oppgave.Tittel}
                  badge={
                    !oppgave.Lest && (
                      <Badge>{resources?.badge_ulest || ""}</Badge>
                    )
                  }
                />
                <Panel.PreContainer>
                  <div className={styles.panel_precontainer}>
                    <StatusDot
                      text={getStatusText(oppgave.Status, resources)}
                      variant={getStatusDotVariant(oppgave.Status)}
                      className={`${oppgave.Status === OppgaveStatus.Ready ? styles.statusDot_ready : ""}`}
                    ></StatusDot>
                    {oppgave.SkjulForFullmakt && (
                      <SharingStatus icon={NoEye}>
                        {resources?.sharingstatus_skjult || ""}
                      </SharingStatus>
                    )}
                  </div>
                </Panel.PreContainer>
                <Panel.A>
                  <div className={styles.panel_info_wrapper}>
                    {visFrist(oppgave.Status) && (
                      <div>
                        <div className={styles.panel_label}>
                          {resources?.label_frist_ny || ""}{" "}
                        </div>
                        {createDateString_deadline(resources, oppgave.Frist)}
                      </div>
                    )}
                    {visFullfort(oppgave.Status) && (
                      <div>
                        <div className={styles.panel_label}>
                          {resources?.oppgavestatus_completed || ""}
                          {": "}
                        </div>
                        {createDateString(oppgave.FullfortDato, "dd.MM.yyyy")}
                      </div>
                    )}

                    {visKansellert(oppgave.Status) && (
                      <div>
                        <div className={styles.panel_label}>
                          {resources?.oppgavestatus_cancelled || ""}
                          {": "}
                        </div>
                        {createDateString(oppgave.KansellertDato, "dd.MM.yyyy")}
                      </div>
                    )}

                    <div>
                      <div className={styles.panel_label}>
                        {resources?.label_avsender || ""}{" "}
                      </div>
                      {oppgave.KontaktNavn}
                    </div>
                    {visInstruksjon(oppgave.IntegrertVisning) && (
                      <div>
                        <div className={styles.panel_label}>
                          {resources?.label_oppgave || ""}{" "}
                        </div>
                        {getOppgaveInstruksjon(oppgave.Type, resources)}
                      </div>
                    )}
                  </div>
                </Panel.A>
                <Panel.B>
                  <Button
                    variant="borderless"
                    onClick={() => navigate(`${OPPGAVER_PATH}/${oppgave.Id}`)}
                  >
                    {resources?.knapp_til_oppgave || ""}
                    <Icon svgIcon={ArrowRight} />
                  </Button>
                </Panel.B>
              </Panel>
            ),
        )}
      </PanelList>
      {visFlereOppgaverKnapp && (
        <Button
          className={styles.vis_flere_button}
          variant="outline"
          type="button"
          onClick={(e) => {
            e?.preventDefault();
            setPageNumber(pageNumber + 1);
          }}
        >
          {resources?.knapp_vis_flere || ""}
        </Button>
      )}
    </div>
  );
};
